/**
 * useInvestigationStore — Zustand store for auto-investigation state.
 *
 * Extracted from Workbench.tsx as part of Phase 0 full.
 *
 * Design: useAutoInvestigation() hook stays in Workbench (complex SSE/cancellation
 * logic). This store wraps the hook's return value so other components can read
 * investigation state without prop drilling.
 *
 * Workbench calls syncInvestigation() in a useEffect to keep the store in sync.
 * Other components (AutoInvestigateCard, TriageRibbon, filter effect) read from here.
 */

import { create } from 'zustand';
import type { UseAutoInvestigationReturn } from '../hooks/useAutoInvestigation';

export interface InvestigateRequest {
    /** Address to investigate (normalized canvas id). */
    address: string;
    /** Optional chain hint; AutoInvestigateCard falls back to detectChain. */
    chain?: string | null;
    /** Monotonic seq so repeat right-clicks on the same node re-fire. */
    seq: number;
}

export interface InvestigationState {
    /** Whether the AutoInvestigateCard floating panel is visible. */
    showAutoInvestigateCard: boolean;

    /** Root address for canvas tiering (normalized). Set when investigation completes. */
    investigationRootId: string | null;

    /** Mirror of useAutoInvestigation() hook return. null until first sync. */
    investigation: UseAutoInvestigationReturn | null;

    /**
     * Pending per-node Auto-Investigate request (right-click → re-run).
     * AutoInvestigateCard consumes this: cancel if running, retarget, runViaStream.
     */
    investigateRequest: InvestigateRequest | null;

    // ── Actions ──
    setShowAutoInvestigateCard: (show: boolean) => void;
    toggleAutoInvestigateCard: () => void;
    setInvestigationRootId: (id: string | null) => void;

    /**
     * Sync the useAutoInvestigation() hook return into the store.
     * Called by Workbench in a useEffect.
     */
    syncInvestigation: (inv: UseAutoInvestigationReturn) => void;

    /** Queue a sequential SSE Auto-Investigate for `address` (never parallelizes steps). */
    requestInvestigate: (address: string, chain?: string | null) => void;

    /** Clear after AutoInvestigateCard starts (or abandons) the request. */
    clearInvestigateRequest: () => void;
}

let _investigateSeq = 0;

export const useInvestigationStore = create<InvestigationState>((set) => ({
    showAutoInvestigateCard: false,
    investigationRootId: null,
    investigation: null,
    investigateRequest: null,

    setShowAutoInvestigateCard: (show) => set({ showAutoInvestigateCard: show }),
    toggleAutoInvestigateCard: () =>
        set((s) => ({ showAutoInvestigateCard: !s.showAutoInvestigateCard })),
    setInvestigationRootId: (id) => set({ investigationRootId: id }),
    syncInvestigation: (inv) => set({ investigation: inv }),
    requestInvestigate: (address, chain = null) => {
        const trimmed = String(address || '').trim();
        if (!trimmed) return;
        _investigateSeq += 1;
        set({
            investigateRequest: { address: trimmed, chain: chain ?? null, seq: _investigateSeq },
            showAutoInvestigateCard: true,
        });
    },
    clearInvestigateRequest: () => set({ investigateRequest: null }),
}));

// Dev-tool exposure — read-only handle for the Ctrl+Shift+E debug snapshot
// (canvasDebug.exportSession reads `window.__useInvestigationStore` to dump
// the latest section results alongside the canvas state). Same pattern as
// `__useGraphStore`, `__useSelectionStore`.
if (typeof window !== 'undefined') {
    (window as any).__useInvestigationStore = useInvestigationStore;
}
